import React, { useEffect, useState } from 'react'
import { FormattedNumber } from 'react-intl'
import { useCurrencyConversion } from 'hooks/useCurrencyConversion'
import { useCurrency } from 'contexts'
import { LazyIcon } from '../../uikit/web3Icons/LazyTokenIcon'

const cryptoDefaultIconUrl = require('../Icon/assets/cryptoDefault.svg')

export interface IIncome {
  value: number | string
  currency?: string
  isFormatted?: boolean
  className?: string
  byYear?: Record<string, number>
  useCurrencyConversion?: boolean
  year?: number
}

export const getAbbreviatedNumber = (number_input: number | string) => {
  let originalNumber: number
  let number: number
  if (typeof number_input === 'string') {
    originalNumber = parseFloat(number_input)
    number = parseFloat(number_input)
  } else {
    originalNumber = number_input
    number = number_input
  }

  if ((number > 0 && number < 1000) || (number > -1000 && number < 0)) {
    return number % 1 !== 0
      ? { finalNumber: parseFloat(number.toFixed(2)), unit: '' }
      : { finalNumber: number, unit: '' }
  }

  if (number < 0) number = -number

  let abbreviatedNumber = number
  const units = ['K', 'M', 'B', 'T']
  let unit = ''
  let i = 0
  while (abbreviatedNumber >= 1000 && i < units.length) {
    abbreviatedNumber /= 1000
    unit = units[i]
    // eslint-disable-next-line no-plusplus
    i++
  }

  if (originalNumber < 0) abbreviatedNumber = -abbreviatedNumber
  const finalNumber: number =
    abbreviatedNumber % 1 !== 0 ? parseFloat(abbreviatedNumber?.toFixed(2)) : abbreviatedNumber
  return { finalNumber, unit }
}

export const getAbbreviatedNumberString = (num: number) => {
  const parsed = getAbbreviatedNumber(num)

  return `${parsed.finalNumber}${parsed.unit}`
}

const Income = ({
  value,
  currency = 'USD',
  isFormatted = true,
  className,
  byYear,
  useCurrencyConversion: shouldUseCurrencyConversion = false,
  year,
}: IIncome) => {
  const { currentCurrency, convertValueByYear } = useCurrencyConversion()
  const { currencies } = useCurrency()

  const [convertedValue, setConvertedValue] = useState<number | null>(null)
  const [isConverting, setIsConverting] = useState(false)

  const originalValue = typeof value === 'string' ? parseFloat(value) : value
  let displayCurrency = currency
  let displayValue = value

  useEffect(() => {
    if (shouldUseCurrencyConversion) {
      setIsConverting(true)

      if (byYear && Object.keys(byYear).length > 0) {
        // Convert each year separately using that year's rate, then sum
        // CurrencyContext automatically falls back to most recent rate if year is missing
        const convertByYearAsync = async () => {
          let totalConverted = 0

          for (const [yearStr, amount] of Object.entries(byYear)) {
            if (typeof amount === 'number') {
              const yearNum = parseInt(yearStr)
              const converted = await convertValueByYear(amount, yearNum, 'USD', currentCurrency)

              if (converted !== null) {
                totalConverted += converted
              }
            }
          }

          setConvertedValue(totalConverted)
          setIsConverting(false)
        }

        convertByYearAsync().catch((error: Error) => {
          console.error('Error converting currency by year:', error)
          setConvertedValue(null)
          setIsConverting(false)
        })
      } else {
        const yearToUse = year || new Date().getFullYear()

        convertValueByYear(originalValue, yearToUse, 'USD', currentCurrency)
          .then((converted: number | null) => {
            setConvertedValue(converted)
            setIsConverting(false)
          })
          .catch((error: Error) => {
            console.error('Error converting currency:', error)
            setConvertedValue(null)
            setIsConverting(false)
          })
      }
    } else {
      setConvertedValue(null)
    }
  }, [shouldUseCurrencyConversion, byYear, currentCurrency, convertValueByYear, originalValue, year])

  if (convertedValue !== null) {
    displayValue = convertedValue
    displayCurrency = currentCurrency
  }

  const getCurrencySymbol = (currencyCode: string): string => {
    const currency = currencies.find((c: { code: string; symbol: string }) => c.code === currencyCode)
    return currency?.symbol || currencyCode
  }

  let suffix = ''
  let formattedValue: number

  if (isFormatted) {
    const { finalNumber, unit } = getAbbreviatedNumber(displayValue)
    formattedValue = finalNumber
    suffix = unit
  } else {
    formattedValue = typeof displayValue === 'string' ? parseFloat(displayValue) : displayValue
  }

  if (isConverting) {
    return <span className={className}>Converting...</span>
  }

  return (
    <span className={className}>
      {displayCurrency === 'ETH' ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <LazyIcon
            symbol='ETH'
            size={16}
            fallback={
              <img
                src={cryptoDefaultIconUrl}
                alt='ETH'
                style={{ width: '16px', height: '16px', objectFit: 'contain' }}
              />
            }
          />
          <FormattedNumber minimumFractionDigits={0} maximumFractionDigits={2} value={formattedValue} />
        </span>
      ) : (
        <span>
          {getCurrencySymbol(displayCurrency)}
          {formattedValue.toLocaleString()}
        </span>
      )}
      <span>{suffix}</span>
    </span>
  )
}

export default Income
